import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useDialogStore } from '@/store/dialogStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import type { WorkspaceId } from '@/types';

/** Rename / delete workspace dialogs, opened from the command palette. */
export const WorkspaceDialogs = () => {
  const request = useDialogStore((s) => s.request);
  if (!request) return null;
  return request.kind === 'rename-workspace' ? <RenameDialog id={request.id} /> : <DeleteDialog id={request.id} />;
};

const useWorkspaceName = (id: WorkspaceId) => useWorkspaceStore((s) => s.workspaces[id]?.workspace.name ?? '');

const RenameDialog = ({ id }: { id: WorkspaceId }) => {
  const current = useWorkspaceName(id);
  const [name, setName] = useState(current);
  const close = () => {
    useDialogStore.getState().close();
  };
  const submit = () => {
    const next = name.trim();
    if (next && next !== current) useWorkspaceStore.getState().renameWorkspace(id, next);
    close();
  };
  return (
    <Dialog
      open
      onClose={close}
      aria-labelledby="rename-workspace-title"
      maxWidth="xs"
      fullWidth
      data-testid="rename-workspace-dialog"
    >
      <DialogTitle id="rename-workspace-title">Rename workspace</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Name"
          value={name}
          margin="dense"
          slotProps={{ htmlInput: { 'data-testid': 'rename-workspace-input', maxLength: 60 } }}
          onChange={(e) => {
            setName(e.target.value);
          }}
          onKeyDown={(e) => {
            // Global shortcuts already ignore editable targets; let Escape reach the Dialog.
            if (e.key === 'Enter') submit();
            else if (e.key === 'Escape') close();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={close}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!name.trim()} data-testid="rename-workspace-confirm" onClick={submit}>
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteDialog = ({ id }: { id: WorkspaceId }) => {
  const name = useWorkspaceName(id);
  const count = useWorkspaceStore((s) => Object.keys(s.workspaces[id]?.objects ?? {}).length);
  const isLast = useWorkspaceStore((s) => Object.keys(s.workspaces).length <= 1);
  const close = () => {
    useDialogStore.getState().close();
  };
  return (
    <Dialog
      open
      onClose={close}
      aria-labelledby="delete-workspace-title"
      maxWidth="xs"
      data-testid="delete-workspace-dialog"
    >
      <DialogTitle id="delete-workspace-title">Delete “{name}”?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isLast
            ? 'The last workspace cannot be deleted.'
            : `This removes the workspace and its ${count} object${count === 1 ? '' : 's'}. This cannot be undone.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={close} autoFocus>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={isLast}
          data-testid="delete-workspace-confirm"
          onClick={() => {
            useWorkspaceStore.getState().deleteWorkspace(id);
            close();
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
